import { useEffect, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import useHasRoutePermission from '@/hooks/useHasRoutePermission';
import staffMemberService, { CreateStaffMemberByPersonIdProps } from '@/services/staffMember';
import { PersonType } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import { Paths } from '@/types/routes';
import CreateStaffMemberProfileModalComponent, {
  Props as ComponentProps,
} from './CreateStaffMemberProfileModal.component';

type Props = Pick<ComponentProps, 'isOpen' | 'onClose' | 'message'> & {
  person: Pick<PersonType, 'id' | 'firstName'>;
  onCreateStaffMember?: (staffMember: StaffMember) => void;
};

const CreateStaffMemberProfileModal = ({
  person,
  onCreateStaffMember,
  isOpen = false,
  onClose,
  message,
}: Props): JSX.Element => {
  const toast = useToastContext();
  const [createdStaffMember, setCreatedStaffMember] = useState<StaffMember>();
  const createStaffMemberMutation = useApiSubmit<StaffMember, CreateStaffMemberByPersonIdProps>(
    staffMemberService.createStaffMemberByPersonId
  );
  const hasStaffMemberPagePermission = useHasRoutePermission(Paths.STAFF_MEMBER_INFO);

  useEffect(() => {
    if (isOpen) {
      setCreatedStaffMember(undefined);
    }
  }, [isOpen, setCreatedStaffMember]);

  const profileUrl = `/people/staff/${createdStaffMember?.id || ''}`;

  const handleCopyProfileUrl = (): void => {
    navigator.clipboard.writeText(`${window.location.origin}${profileUrl}`);
    toast.showSuccessFeedback('Profile link copied to clipboard.');
  };

  const handleConfirm = async (): Promise<void> => {
    try {
      const response = await createStaffMemberMutation.mutateAsync({
        personId: person.id,
        hiringDate: new Date(),
      });

      onCreateStaffMember?.(response);
      setCreatedStaffMember(response);
    } catch {
      toast.showErrorFeedback('There was a problem trying to create the staff member.');
    }
  };

  return (
    <CreateStaffMemberProfileModalComponent
      name={person.firstName}
      onConfirm={handleConfirm}
      isLoading={createStaffMemberMutation.isLoading}
      createdStaffMember={createdStaffMember}
      isOpen={isOpen}
      onClose={onClose}
      onCopyProfileUrl={handleCopyProfileUrl}
      profileUrl={profileUrl}
      hasStaffMemberPagePermission={hasStaffMemberPagePermission}
      message={message}
    />
  );
};

export default CreateStaffMemberProfileModal;
